// src/hooks/useEntitledFootprints.js
//
// The entitled mask for a map (footprint restyle S-b): GET /imagery/entitled,
// fetched again when the active org changes, the include does or the
// with_reference does.
//
// FAIL CLOSED. `entitled` starts as the whole world masked (WORLD_MASKED) and
// goes back to it on any error and on an org change; only a good answer opens
// holes in it. A failed read is `status: "error"`, which the map says with a
// Retry (EntitledMaskError); it is never an unmasked map and never a silent dim.
//
// WITH_REFERENCE (S-c4): a Sentinel-2 dataset the page shows on top, whose
// entitled area the mask then opens. Changing it keeps the mask on screen
// until the new answer lands: never a fully masked frame between answers, so
// switching back to a 10 cm capture leaves the opened mask up for one
// request. A 403 REFERENCE_NOT_ENTITLED is not an error: the hook warns
// (dataset id and code), asks once more without it, and does not send that id
// again until the org or the with_reference changes. Never a loop.
//
// The route decides each dataset's org from the user, not from X-Active-Org,
// so an org switch changes nothing it answers today; the refetch keeps the
// mask right if that changes.
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchEntitled, REFERENCE_NOT_ENTITLED } from "../api/entitledImagery";
import { WORLD_MASKED } from "../lib/map/footprintStyle";
import { useOrgStore } from "../stores/useOrgStore";
import { usePlatformOrgContext } from "../stores/usePlatformOrgContext";

/**
 * @param {{enabled?: boolean, include?: string|null, withReference?: string|null}} [opts]
 * @returns {{entitled: {mask: object, features: object[]}, status: "loading"|"ok"|"error", error: Error|null, retry: () => void}}
 */
export function useEntitledFootprints({ enabled = true, include = null, withReference = null } = {}) {
  const appOrg = useOrgStore((s) => s.activeOrgId);
  const platformOrg = usePlatformOrgContext((s) => s.activeOrgId);
  const org = platformOrg || appOrg || null;
  const [state, setState] = useState({ entitled: WORLD_MASKED, status: "loading", error: null });
  const [attempt, setAttempt] = useState(0);
  const shownOrg = useRef(org);
  // The with_reference refused for this (org, selection): forgotten, during
  // render, as soon as either changes, so the next selection asks afresh.
  const [refused, setRefused] = useState(null);
  const [selection, setSelection] = useState({ org, withReference });
  if (selection.org !== org || selection.withReference !== withReference) {
    setSelection({ org, withReference });
    setRefused(null);
  }
  const sentReference = withReference && withReference !== refused ? withReference : null;

  useEffect(() => {
    if (!enabled) return undefined;
    const ctrl = new AbortController();
    // Another org's holes are not this org's: mask the world until it answers.
    const orgChanged = shownOrg.current !== org;
    shownOrg.current = org;
    setState((s) => ({ entitled: orgChanged ? WORLD_MASKED : s.entitled, status: "loading", error: null }));
    fetchEntitled({ include, withReference: sentReference, signal: ctrl.signal })
      .then((entitled) => { if (!ctrl.signal.aborted) setState({ entitled, status: "ok", error: null }); })
      .catch((error) => {
        if (ctrl.signal.aborted) return;
        if (sentReference && error?.code === REFERENCE_NOT_ENTITLED) {
          // Ask once more without it; the mask on screen stays until then.
          console.warn("[footprints] with_reference refused", { dataset_id: sentReference, code: error.code });
          setRefused(sentReference);
          return;
        }
        console.warn("[footprints] entitled imagery failed to load", error);
        setState({ entitled: WORLD_MASKED, status: "error", error });
      });
    return () => ctrl.abort();
  }, [enabled, include, sentReference, org, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
